import { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import StyledTable, { ColumnHeader } from "../../../components/table/styled-table";
import SearchBox from "../../../components/search-box";
import { configs } from "../../../app/app-configs";
import {
    fetchMyAttendancePage,
    fetchStaffAttendancesPage,
    selectIsLoading,
    selectTotalPages,
} from "../staff-attendance.slice";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import StaffAttendanceTableRow from "./staff-attendance-table-row";
import { getDefaultStaffAttendanceSearchCriteria, StaffAttendance } from "../../../service/model/staff-attendance.model";
import DateRangeInput from "../../../components/form-input-field-wrappers/date-range-input";

const headers: ColumnHeader[] = [
    { name: "Name" },
    { name: "Check In" },
    // { name: "Check Out" },
    { name: "Marked By" },
    { name: "Source" },
    { name: "Actions", className: "action-header" },
];

interface TableProps {
    readonly staffAttendances: StaffAttendance[];
    readonly isProfileView?: boolean;
}

const StaffAttendanceTable: React.FC<TableProps> = ({ staffAttendances, isProfileView }) => {
    const dispatch = useAppDispatch();
    const totalPages = useAppSelector(selectTotalPages);
    const isLoading = useAppSelector(selectIsLoading);

    const [page, setPage] = useState(1);
    const [searchText, setSearchText] = useState("");

    const [dateRange, setDateRange] = useState({
        startDate: new Date(getDefaultStaffAttendanceSearchCriteria().from),
        endDate: new Date(getDefaultStaffAttendanceSearchCriteria().to),
    });

    useEffect(() => {
        if (isProfileView) {
            dispatch(fetchMyAttendancePage({
                pagination: { page: page - 1, size: configs.PAGE_SIZE },
                name: '',
                from: dateRange.startDate.toISOString().slice(0, 10),
                to: dateRange.endDate.toISOString().slice(0, 10)
            }));
        } else {
            dispatch(fetchStaffAttendancesPage({
                pagination: { page: page - 1, size: configs.PAGE_SIZE },
                name: searchText,
                from: dateRange.startDate.toISOString().slice(0, 10),
                to: dateRange.endDate.toISOString().slice(0, 10)
            }))
        }
    }, [dispatch, page, searchText, dateRange, isProfileView]);

    const filteredAttendances = useMemo(() => {
        const text = searchText.trim().toLowerCase();

        if (!text) {
            return staffAttendances;
        }

        return staffAttendances.filter((staffAttendance) => {
            return (
                staffAttendance.businessUserName?.toLowerCase().includes(text) ||
                staffAttendance.markedBy?.toLowerCase().includes(text) ||
                staffAttendance.source?.toLowerCase().includes(text)
            );
        });
    }, [searchText, staffAttendances]);

    const handlePageChange = (selectedPage: number) => {
        setPage(selectedPage);
    };

    const handleDateChange = (start: Date | null, end: Date | null) => {
        setDateRange({ startDate: start ?? new Date(), endDate: end ?? new Date() });
    };


    return (
        <>
            <Grid container direction="row" style={{ marginTop: "12px", alignItems: "center" }}>
                {!isProfileView && (
                    <Grid
                        size={{ lg: 6, md: 12, sm: 12 }}
                        sx={{ width: "100%" }}
                    >
                        <SearchBox placeholder="Search by staff name" setSearchValue={setSearchText} />
                    </Grid>
                )}

                <DateRangeInput
                    initialStartDate={dateRange.startDate}
                    initialEndDate={dateRange.endDate}
                    onDateChange={handleDateChange}
                />
            </Grid>

            <StyledTable
                headers={headers}
                currentPage={page}
                totalPages={totalPages}
                loading={isLoading}
                emptyMessage="No attendance records found."
                onPageChange={handlePageChange}
            >
                {filteredAttendances?.map((staffAttendance) => (
                    <StaffAttendanceTableRow
                        staffAttendance={staffAttendance}
                        key={staffAttendance.id}
                        isProfileView={isProfileView}
                    />
                ))}
            </StyledTable>
        </>
    );
};

export default StaffAttendanceTable;
